import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { triggerAcknowledgmentPulse } from "./cycle/AcknowledgmentPulse";

// FEATURE 4: Symptom Pattern Detection
function detectSymptomPattern() {
  const keys = Object.keys(localStorage).filter(k => k.startsWith('pcosSymptoms_'));
  if (keys.length < 3) return null;

  const logs = keys.map(k => JSON.parse(localStorage.getItem(k)));
  const fields = ['acne','fatigue','moodSwings','cravings','bloating','irregularPeriods','hairLoss','brainFog',
                  'acne','hair_loss','mood','fatigue','sleep_quality','weight_change']; // Include both schemas
  const avg = {};
  
  // Calculate averages for all possible fields
  ['acne','fatigue','mood','moodSwings','cravings','bloating','irregularPeriods','hairLoss','hair_loss','brainFog','sleep_quality','weight_change'].forEach(f => {
    const values = logs.map(l => l[f] || l.symptoms?.[f] || 0).filter(v => !isNaN(v));
    avg[f] = values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
  });

  // Normalize field names
  if (!avg.irregularPeriods) avg.irregularPeriods = 0;
  if (!avg.hairLoss) avg.hairLoss = avg.hair_loss || 0;
  if (!avg.moodSwings) avg.moodSwings = avg.mood || 0;
  if (!avg.brainFog) avg.brainFog = avg.sleep_quality || 0;

  const patterns = [];
  if (avg.irregularPeriods >= 2 && avg.acne >= 2) patterns.push('Androgenic Pattern');
  if (avg.fatigue >= 2 && avg.cravings >= 2 && avg.bloating >= 2) patterns.push('Insulin Resistance Pattern');
  if ((avg.moodSwings >= 2 || avg.mood >= 2) && (avg.brainFog >= 2 || avg.sleep_quality >= 2)) patterns.push('Inflammatory or Stress Pattern');
  if (patterns.length === 0) patterns.push('No Strong Pattern Detected');

  const allFields = ['acne','fatigue','mood','hairLoss','hair_loss','sleep_quality','weight_change'];
  const topSymptom = allFields.reduce((a, b) => (avg[a] || 0) > (avg[b] || 0) ? a : b);
  const result = { patterns, topSymptom, averages: avg, logCount: logs.length };
  localStorage.setItem('pcosSymptomPattern', JSON.stringify(result));
  return result;
}

// Helper to convert camelCase to Title Case
function toTitleCase(str) {
  return str
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

const symptomLibrary = [
  // Category: MENSTRUAL
  { key: "cramps",           label: "Cramps",              emoji: "🩸", category: "Menstrual" },
  { key: "heavy_flow",       label: "Heavy flow",          emoji: "🌊", category: "Menstrual" },
  { key: "spotting",         label: "Spotting",            emoji: "💧", category: "Menstrual" },
  { key: "irregular_period", label: "Irregular period",    emoji: "🌀", category: "Menstrual" },
  { key: "pelvic_pain",      label: "Pelvic pain",         emoji: "⚡", category: "Menstrual" },

  // Category: HORMONAL (the original 6 core symptoms live here where relevant)
  { key: "acne",             label: "Acne",                emoji: "🌿", category: "Hormonal" },
  { key: "hair_loss",        label: "Hair loss",           emoji: "💇", category: "Hormonal" },
  { key: "excess_hair",      label: "Excess hair growth",  emoji: "🪒", category: "Hormonal" },
  { key: "breast_tenderness",label: "Breast tenderness",   emoji: "🌸", category: "Hormonal" },
  { key: "hot_flashes",      label: "Hot flashes",         emoji: "🔥", category: "Hormonal" },

  // Category: METABOLIC
  { key: "weight_change",    label: "Weight change",       emoji: "⚖️", category: "Metabolic" },
  { key: "cravings",         label: "Cravings",            emoji: "🍫", category: "Metabolic" },
  { key: "bloating",         label: "Bloating",            emoji: "🎈", category: "Metabolic" },
  { key: "sugar_dips",       label: "Sugar dips",          emoji: "📉", category: "Metabolic" },

  // Category: EMOTIONAL
  { key: "mood",             label: "Mood swings",         emoji: "😊", category: "Emotional" },
  { key: "anxiety",          label: "Anxiety",             emoji: "🌪️", category: "Emotional" },
  { key: "low_mood",         label: "Low mood",            emoji: "☁️", category: "Emotional" },
  { key: "irritability",     label: "Irritability",        emoji: "⚡", category: "Emotional" },
  { key: "brain_fog",        label: "Brain fog",           emoji: "🌫️", category: "Emotional" },

  // Category: DIGESTIVE
  { key: "nausea",           label: "Nausea",              emoji: "🤢", category: "Digestive" },
  { key: "constipation",     label: "Constipation",        emoji: "🚫", category: "Digestive" },
  { key: "diarrhea",         label: "Diarrhea",            emoji: "💦", category: "Digestive" },
  { key: "reflux",           label: "Acid reflux",         emoji: "🔥", category: "Digestive" },

  // Category: SLEEP
  { key: "sleep_quality",    label: "Sleep quality",       emoji: "🌙", category: "Sleep" },
  { key: "insomnia",         label: "Insomnia",            emoji: "🌜", category: "Sleep" },
  { key: "night_sweats",     label: "Night sweats",        emoji: "💦", category: "Sleep" },

  // Category: LIFESTYLE
  { key: "fatigue",          label: "Fatigue",             emoji: "😴", category: "Lifestyle" },
  { key: "headache",         label: "Headache",            emoji: "🤕", category: "Lifestyle" },
  { key: "back_pain",        label: "Back pain",           emoji: "🦴", category: "Lifestyle" },
  { key: "muscle_soreness",  label: "Muscle soreness",     emoji: "💪", category: "Lifestyle" },
  { key: "low_libido",       label: "Low libido",          emoji: "🌑", category: "Lifestyle" },

  // Category: FERTILITY
  { key: "cervical_mucus",   label: "Cervical mucus",      emoji: "💧", category: "Fertility" },
  { key: "ovulation_pain",   label: "Ovulation pain",      emoji: "🎯", category: "Fertility" },
  { key: "temperature_shift",label: "Temperature shift",   emoji: "🌡️", category: "Fertility" },
];

// Columns that exist in the Supabase `symptom_logs` table today. Only
// these keys are persisted to that table's columns. All other keys are
// stored in localStorage for pattern detection and, when we roll a
// JSONB `extras` migration, will move to cloud persistence.
const SYMPTOM_LOG_COLUMNS = ['acne', 'hair_loss', 'mood', 'fatigue', 'sleep_quality', 'weight_change'];
const symptoms = symptomLibrary;
const symptomCategoryOrder = ['Menstrual', 'Hormonal', 'Metabolic', 'Emotional', 'Digestive', 'Sleep', 'Lifestyle', 'Fertility'];
const severityLabels = ["None", "Mild", "Moderate", "Severe"];
const severityColors = ["#D1FAE5", "#FEF9C3", "#FED7AA", "#FDE8E8"];
const severityTextColors = ["#065F46", "#854D0E", "#92400E", "#9B1C1C"];

export default function SymptomsScreen({ userId }) {
  const [scores, setScores] = useState(() => {
    const init = {};
    symptoms.forEach((s) => { init[s.key] = 0; });
    return init;
  });
  const [existingId, setExistingId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [saving, setSaving] = useState(false);
  const [symptomPattern, setSymptomPattern] = useState(null);
  const [symptomHistory, setSymptomHistory] = useState([]);
  const didFetch = useRef(false);

  // Calculate symptom trends from history
  const getSymptomTrends = () => {
    if (symptomHistory.length < 2) return null;
    
    const trends = {};
    symptoms.forEach(s => {
      const values = symptomHistory.map(h => h[s.key] ?? 0);
      const firstHalf = values.slice(0, Math.ceil(values.length / 2));
      const secondHalf = values.slice(Math.floor(values.length / 2));
      
      const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
      const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
      const diff = secondAvg - firstAvg;
      
      let trend = 'stable';
      let icon = '→';
      let color = '#8B5CF6';
      let bgColor = '#EDE9FE';
      
      if (diff > 0.4) {
        trend = 'increasing';
        icon = '↑';
        color = '#DC2626';
        bgColor = '#FEE2E2';
      } else if (diff < -0.4) {
        trend = 'decreasing';
        icon = '↓';
        color = '#059669';
        bgColor = '#D1FAE5';
      }
      
      trends[s.key] = { 
        trend, 
        icon, 
        color, 
        bgColor,
        current: values[values.length - 1],
        average: (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)
      };
    });
    
    return trends;
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    const fetchToday = async () => {
      const today = new Date().toISOString().split("T")[0];
      // Attempt to select the `extras` JSONB column too. If the migration
      // hasn't been run in this Supabase project, PostgREST will reject
      // the unknown column and we fall back to the columns-only select.
      let { data, error } = await supabase
        .from("symptom_logs")
        .select("id,acne,hair_loss,mood,fatigue,sleep_quality,weight_change,extras")
        .eq("user_id", userId)
        .eq("log_date", today)
        .maybeSingle();
      if (error && /extras/i.test(error.message || "")) {
        const fallback = await supabase
          .from("symptom_logs")
          .select("id,acne,hair_loss,mood,fatigue,sleep_quality,weight_change")
          .eq("user_id", userId)
          .eq("log_date", today)
          .maybeSingle();
        data = fallback.data;
      }
      if (data) {
        setExistingId(data.id);
        const loaded = {};
        symptoms.forEach((s) => { loaded[s.key] = data[s.key] ?? 0; });
        // Prefer DB `extras` JSONB when present; localStorage overlay
        // is retained only as a fallback for users on older schema.
        const extras = data.extras && typeof data.extras === 'object' ? data.extras : null;
        if (extras) {
          Object.keys(extras).forEach((k) => { if (loaded[k] === 0) loaded[k] = extras[k] || 0; });
        } else {
          const overlay = JSON.parse(localStorage.getItem(`pcosSymptoms_${today}`) || '{}');
          Object.keys(overlay).forEach((k) => { if (loaded[k] === 0) loaded[k] = overlay[k] || 0; });
        }
        setScores(loaded);
      }
    };
    fetchToday();

    // FEATURE 4: Detect symptom pattern on mount
    const pattern = detectSymptomPattern();
    if (pattern) setSymptomPattern(pattern);
    
    // Load symptom history from localStorage
    const historyKeys = Object.keys(localStorage)
      .filter(k => k.startsWith('pcosSymptoms_'))
      .sort()
      .slice(-14); // Last 14 days
    
    const history = historyKeys.map(k => {
      const date = k.replace('pcosSymptoms_', '');
      return { date, ...JSON.parse(localStorage.getItem(k) || '{}') };
    });
    setSymptomHistory(history);
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    const today = new Date().toISOString().split("T")[0];

    // Split scores into DB-columns vs extras
    const columnScores = {};
    const extras = {};
    SYMPTOM_LOG_COLUMNS.forEach((k) => { columnScores[k] = scores[k] || 0; });
    Object.keys(scores).forEach((k) => {
      if (!SYMPTOM_LOG_COLUMNS.includes(k) && scores[k]) extras[k] = scores[k];
    });

    // Try write WITH extras first. If the column is missing on the DB,
    // retry without it so nothing breaks — extras stay in localStorage
    // until the migration is run (see SUPABASE_SYMPTOM_EXTRAS.sql).
    const writeWithFallback = async (payload) => {
      if (existingId) {
        let res = await supabase.from("symptom_logs").update(payload).eq("id", existingId);
        if (res.error && /extras/i.test(res.error.message || "")) {
          const { extras: _drop, ...rest } = payload;
          res = await supabase.from("symptom_logs").update(rest).eq("id", existingId);
        }
        return res;
      }
      let ins = await supabase
        .from("symptom_logs")
        .insert({ user_id: userId, log_date: today, ...payload })
        .select("id")
        .single();
      if (ins.error && /extras/i.test(ins.error.message || "")) {
        const { extras: _drop, ...rest } = payload;
        ins = await supabase
          .from("symptom_logs")
          .insert({ user_id: userId, log_date: today, ...rest })
          .select("id")
          .single();
      }
      if (ins.data) setExistingId(ins.data.id);
      return ins;
    };

    await writeWithFallback({ ...columnScores, extras });

    // Keep the localStorage cache in sync with a best-effort mirror —
    // used as a fallback if the DB `extras` column is unavailable AND
    // by the legacy `detectSymptomPattern` reader (to be retired in
    // Phase C when the DB-driven pattern engine ships).
    localStorage.setItem(`pcosSymptoms_${today}`, JSON.stringify(scores));

    // Re-detect pattern after new log
    const pattern = detectSymptomPattern();
    if (pattern) setSymptomPattern(pattern);

    setSaving(false);
    setShowToast(true);
    // Living Companion acknowledgment — soft orb-tone pulse
    try { triggerAcknowledgmentPulse({ tone: 'brand' }); } catch (_) { /* pulse host not mounted (non-cycle route) */ }
    setTimeout(() => setShowToast(false), 2500);
  };

  return (
    <div className="symptoms-screen fade-in" data-testid="symptoms-screen">
      <h2>Log Today&apos;s Symptoms</h2>
      <p className="screen-subtitle">Tracking patterns over time helps identify trends.</p>

      {symptomCategoryOrder.map((cat) => {
        const catSymptoms = symptoms.filter((s) => s.category === cat);
        if (catSymptoms.length === 0) return null;
        return (
          <div key={cat} className="symptom-category" data-testid={`symptom-cat-${cat.toLowerCase()}`}>
            <h3 style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: '#8A8A9A', margin: '20px 0 10px', padding: '0 4px' }}>{cat}</h3>
            {catSymptoms.map((s, index) => (
              <div key={s.key} className={`symptom-card fade-in stagger-${(index % 5) + 1}`} data-testid={`symptom-${s.key}`}>
                <div className="symptom-top">
                  <span className="symptom-name">{s.emoji} {s.label}</span>
                  <span className="symptom-severity" style={{
                    background: severityColors[scores[s.key]],
                    color: severityTextColors[scores[s.key]],
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    transition: 'all 0.2s ease'
                  }}>{severityLabels[scores[s.key]]}</span>
                </div>
                <input
                  type="range" min="0" max="3" step="1"
                  value={scores[s.key]}
                  onChange={(e) => setScores((p) => ({ ...p, [s.key]: parseInt(e.target.value) }))}
                  className="symptom-slider"
                  data-testid={`slider-${s.key}`}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  {severityLabels.map((label, idx) => (
                    <span key={label} style={{
                      fontSize: 10,
                      color: scores[s.key] === idx ? severityTextColors[idx] : '#C4B5FD',
                      fontWeight: scores[s.key] === idx ? 600 : 400,
                      transition: 'all 0.2s ease'
                    }}>{label}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      <button className="btn-primary" onClick={handleSave} disabled={saving} style={{ marginTop: 24 }} data-testid="save-symptoms-btn">
        {saving ? "Saving\u2026" : "Save Today's Log"}
      </button>

      {/* FEATURE 4: Symptom Pattern Card */}
      {symptomPattern && symptomPattern.logCount >= 3 && (
        <div style={{
          background: '#fff', borderRadius: 20, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
          padding: '20px 24px', marginTop: 24
        }} data-testid="symptom-pattern-card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Your Symptom Pattern</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {symptomPattern.patterns.map((p, idx) => (
              <span key={idx} style={{
                background: '#EDE9F8', color: '#6D5EF5', borderRadius: 20,
                padding: '6px 14px', fontSize: 13, display: 'inline-flex'
              }}>
                {p}
              </span>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #F0EEF8', paddingTop: 12, marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: '#5A5A5A' }}>Most frequent symptom: </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#6D5EF5' }}>{toTitleCase(symptomPattern.topSymptom)}</span>
          </div>
          <p style={{ fontSize: 11, fontStyle: 'italic', color: '#8A8A9A', margin: 0 }}>
            Based on your last {symptomPattern.logCount} logs. Patterns are informational only.
          </p>
        </div>
      )}

      {/* Symptom History & Trends */}
      {symptomHistory.length >= 2 && (
        <div style={{
          background: '#fff', borderRadius: 20, boxShadow: '0 4px 24px rgba(109,94,245,0.09)',
          padding: '20px 24px', marginTop: 20
        }} data-testid="symptom-history-card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Symptom Trends</h3>
          <p style={{ fontSize: 12, color: '#8A8A9A', marginBottom: 16 }}>
            Based on your last {symptomHistory.length} days of logs
          </p>
          
          {/* Trend Cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {symptoms.map(s => {
              const trends = getSymptomTrends();
              if (!trends) return null;
              const trend = trends[s.key];
              
              return (
                <div key={s.key} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  background: '#F9FAFB',
                  borderRadius: 12,
                  borderLeft: `3px solid ${trend.color}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 16 }}>{s.emoji}</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#2F2F35' }}>{s.label}</div>
                      <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                        Avg: {severityLabels[Math.round(parseFloat(trend.average))]} ({trend.average})
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Mini sparkline visualization */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 24 }}>
                      {symptomHistory.slice(-7).map((h, idx) => (
                        <div key={idx} style={{
                          width: 4,
                          height: Math.max(4, ((h[s.key] ?? 0) / 3) * 24),
                          background: trend.color,
                          borderRadius: 2,
                          opacity: 0.3 + (idx / 7) * 0.7
                        }} />
                      ))}
                    </div>
                    {/* Trend badge */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 10px',
                      borderRadius: 12,
                      background: trend.bgColor,
                      color: trend.color,
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                      <span style={{ fontSize: 14 }}>{trend.icon}</span>
                      <span style={{ textTransform: 'capitalize' }}>{trend.trend}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Recent History List */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', marginBottom: 10 }}>
              Recent Logs
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {symptomHistory.slice(-5).reverse().map((entry, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  background: idx === 0 ? '#EDE9FE' : '#F9FAFB',
                  borderRadius: 10
                }}>
                  <span style={{ fontSize: 13, color: '#374151', fontWeight: idx === 0 ? 600 : 400 }}>
                    {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {idx === 0 && <span style={{ color: '#8B5CF6', marginLeft: 6 }}>(Today)</span>}
                  </span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {symptoms.slice(0, 4).map(s => (
                      <span key={s.key} style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        background: severityColors[entry[s.key] ?? 0],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10
                      }} title={`${s.label}: ${severityLabels[entry[s.key] ?? 0]}`}>
                        {entry[s.key] ?? 0}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <p style={{ fontSize: 11, fontStyle: 'italic', color: '#9CA3AF', marginTop: 12, textAlign: 'center' }}>
            Log daily to see more accurate trends over time
          </p>
        </div>
      )}

      {showToast && (
        <div className="toast-container" data-testid="symptom-toast">
          <div className="toast">Symptoms saved ✓</div>
        </div>
      )}
    </div>
  );
}
